/**
 * "Notification sagas" handle received notifications.
 * Each saga handles a single websocket notification that may be received from
 *    the backend.
 *
 * There may not be an established webRTC session for these sagas. This may be
 *    because (1) the notification is a new incoming call, or (2) there is a
 *    de-sync between SDK state and server state. This may or may not be
 *    considered as an error scenario (eg. a "call ended" notification for a
 *    call the SDK doesn't know about may be safely ignored).
 */

// Call plugin.
import { callActions } from '../../call/interfaceNew/actions'
import { MAKE_CALL_FINISH, END_CALL_FINISH } from '../../call/interfaceNew/actionTypes'
import { getOptions, getTurnInfo, getCallByWrtcsSessionId } from '../../call/interfaceNew/selectors'
import { CALL_STATES, COMPLEX_OPERATION_MESSAGES, STATUS_CODES } from '../../call/constants'
import { OPERATIONS, OP_STATUS } from '../../call/interfaceNew/constants'
import { getAllRemoteTracks } from '../sdp/remoteTracks'

// Callstack plugin.
import * as negotiation from './negotiation'
import runPipeline from '../sdp/pipeline'

// Other plugins
import { logManager } from '../../logs'
import { setupIncomingCall } from '../webrtc/establish'
import { closeCall } from '../webrtc/midcall'
import BasicError, { callCodes } from '../../errors'

// Libraries.
import { call, delay, put, race, select, take } from 'redux-saga/effects'
import { v4 as uuidV4 } from 'uuid'
import sdpTransform from 'sdp-transform'

// Helpers
// TODO: Move this to a shared location.
import { generateEndParams } from '../utils/index.js'

/**
 * A "call incoming" notification has been received and needs to be handled.
 *
 * This saga is intended to be the entry point for all "call incoming"
 *    notifications. A "call incoming" notification is the initial offer to
 *    establish a call with a remote endpoint. It represents the start of both
 *    the call flow and webRTC negotiation process for the local side.
 * Handling the notification will create a new call in state.
 * Responsibilities:
 *    1. Have the callstack perform the required webRTC negotiation process.
 *        - Regular or Slow Start negotiation.
 *    2. Respond that the call has been received (ie. ringing).
 *    3. Create call state (via redux action).
 * @method incomingCall
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateCallRinging "Update call" signalling function.
 * @param {Object}   params        Parameters describing the incoming call.
 * @param {string}   [params.sdp]  The remote SDP offer included with the notification (if any).
 * @param {string}   params.wrtcsSessionId ID that the server uses to identify the session.
 * @param {string}   params.remoteNumber   Number of the remote participant.
 * @param {string}   params.remoteName     Display name of the remote participant.
 * @param {string}   params.calleeNumber  Number of the intended call recipient
 */
export function * incomingCall (deps, params, channel) {
  const requests = deps.requests
  const { sdp, wrtcsSessionId, remoteNumber, remoteName, calleeNumber, customParameters } = params
  const callConfig = yield select(getOptions)

  const callId = yield call(uuidV4)

  const log = logManager.getLogger('CALL', callId)
  log.info('Received new incoming call; initiating.', { wrtcsSessionId })

  let mediaOffered = null
  if (sdp) {
    // For a regular call, parse the SDP & collect what media has been offered by the caller:
    // We only support two types
    mediaOffered = { audio: false, video: false }
    sdpTransform.parse(sdp).media.forEach(aMedia => {
      mediaOffered[aMedia.type] = true
    })
  }

  // Dispatch the action right away so the call is in state at this point.
  yield put(
    callActions.callIncoming(callId, {
      remoteParticipant: {
        displayName: remoteName,
        displayNumber: remoteNumber
      },
      to: calleeNumber,
      // Number of the remote participant when the call was established.
      from: remoteNumber,
      // The ID that the backend uses to track this webRTC session.
      wrtcsSessionId,

      // Whether the call was received as a slow start call or not.
      isSlowStart: !sdp,

      // Capture what the caller offered in terms of media
      // For a slow-start call, this will be null.
      mediaOffered
    })
  )

  // Dispatch a custom parameters received action/event if any custom parameters were received as part of the notification
  if (customParameters) {
    const customNames = customParameters.map(param => param.name)
    log.debug(`Received custom parameters as part of the Call: ${customNames}.`)
    yield put(
      callActions.customParametersReceived(callId, {
        customParameters
      })
    )
  }

  /**
   * An incoming call may or may not have an SDP offer associated with it.
   * If it has an SDP, then it is a "regular" call scenario and can be handled
   *    as a normal webRTC negotiation.
   * If it has no SDP, then it is a "slow start" call scenario. In a slow start
   *    scenario, the signaling server acts as a webRTC-middleman, requiring
   *    both sides to provide it with offers and generating the answers itself.
   */
  if (sdp) {
    // Regular call.
    const turnInfo = yield select(getTurnInfo)

    // Since we have the remote offer SDP, we can setup a webRTC session.
    const { error } = yield call(setupIncomingCall, deps, {
      offer: {
        sdp,
        type: 'offer'
      },
      trickleIceMode: callConfig.trickleIceMode,
      defaultPeerConfig: callConfig.defaultPeerConfig,
      turnInfo,
      callId,
      removeBundling: callConfig.removeBundling,
      serverTurnCredentials: callConfig.serverTurnCredentials
    })

    if (error) {
      log.info(`Failed to initiate incoming call. Changing to ${CALL_STATES.ENDED}.`)
      yield put(
        callActions.endCallFinish(callId, {
          isLocal: true,
          error
        })
      )
      return
    }
  } else {
    log.debug('Incoming call is a slow-start call.')
    // Slow start call.
    /*
     * We can't setup a webRTC session yet because generating an offer requires
     *   media constraints. We need to wait until the application provides
     *   media information before we can setup the call.
     */
  }

  // Next state will be Initiated normally, or Ringing if ringing feedback is sent.
  let nextState = CALL_STATES.INITIATED

  // If in 'auto' mode for `ringingFeedbackMode`, send the feedback immediately.
  if (callConfig.ringingFeedbackMode === 'auto') {
    log.info('Sending ringing feedback automatically for incoming call.')
    const callInfo = { wrtcsSessionId, id: callId }
    const ringingResponse = yield call(requests.updateCallRinging, callInfo)

    if (ringingResponse.error) {
      log.info(`Failed to send ringing feedback - ${ringingResponse.error.code}: ${ringingResponse.error.message} `)
    } else {
      log.debug('Successfully sent ringing feedback.')
      nextState = CALL_STATES.RINGING
    }
  }

  log.info(`Finished initiating incoming call. Changing to ${nextState} and waiting on local answer.`)
  yield put(
    callActions.updateCall(callId, {
      state: nextState
    })
  )

  // Workaround for re-syncing incoming calls received via PUSH.
  // TODO: Check if this can be removed once we start receiving TURN credentials with incoming call notifications.
  if (channel && channel.toLowerCase() === 'push') {
    yield put(callActions.resync(callId))
  }
}

/**
 * A "call request" notification has been received for an established call
 *    and needs to be handled.
 *
 * This saga is intended to be the entry point for all "call request"
 *    notifications received from the call's remote side. This does not include
 *    "call incoming" notifications, and so it is assumed the call has already
 *    been established. A "call request" notification is the offer from a remote
 *    operation, and so represents the start of the webRTC renegotiation process
 *    for the local side.
 * This saga does not handle the request itself. It determines if it should be
 *    handled and what process is needed to handle it.
 * Responsibilities:
 *    1. Ensure this is a valid request notification.
 *        - Call exists, Call is not ended, TODO: Call is able to receive a request.
 *    2. Determine how the request should be handled.
 *        - Regular or slow start negotiation process.
 *    3. Pass the request off to the appropriate handler.
 * @method parseCallRequest
 * @param {Object} deps         Dependencies that the saga uses.
 * @param {Object} params       Parameters describing the incoming call.
 * @param {string} [params.sdp] The remote SDP offer included with the notification (if any).
 * @param {string} params.wrtcsSessionId ID that the server uses to identify the session.
 * @param {string} params.remoteName   Name of the remote participant.
 * @param {string} params.remoteNumber Number of the remote participant.
 */
export function * parseCallRequest (deps, params) {
  const { wrtcsSessionId, sdp, remoteName, remoteNumber, customParameters } = params
  const targetCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)

  const log = logManager.getLogger('CALL', (targetCall || {}).id)
  log.info('Received new update call request; handling.', { wrtcsSessionId })

  if (!targetCall) {
    // Scenario: No call is associated with the wrtcsSessionId.
    log.info('Update request is for unknown wrtcsSession. Ignoring.', { wrtcsSessionId })
    return
  } else if (targetCall.state === CALL_STATES.ENDED) {
    // Scenario: The associated call is ended, and should not have an active
    //    webRTC session.
    log.info('Update request is for ended call. Ignoring.', { wrtcsSessionId })
    return
  } else if ([CALL_STATES.RINGING, CALL_STATES.INITIATED, CALL_STATES.EARLY_MEDIA].includes(targetCall.state)) {
    // Scenario: We have received a remote offer for a midcall operation before
    //    the call has been established.
    const localOp = targetCall.localOp
    if (localOp && localOp.status === OP_STATUS.PENDING && localOp.operation === OPERATIONS.MAKE) {
      // The call is waiting for the call answer, so delay the new request until
      //    that is processed. This is a timing issue where the update is received
      //    too early.
      log.info('Update request received before call establishment. Delaying.', { wrtcsSessionId })
      yield call(delayMidCall, deps, params)
      return
    } else {
      // This case should never happen.
    }
  }
  // TODO: Make sure the call is able to receive a `respondCallRequest`
  //    notification (ie. has no pending operation).
  log.debug(`Update request ${sdp ? 'with' : 'without'} SDP for call. Processing.`)

  // Dispatch a custom parameters received action/event if any custom parameters were received as part of the notification
  if (customParameters) {
    const customNames = customParameters.map(param => param.name)
    log.debug(`Received custom parameters as part of the Call: ${customNames}.`)
    yield put(
      callActions.customParametersReceived(targetCall.id, {
        customParameters
      })
    )
  }

  /**
   * How the request should be handled depends on whether it includes an SDP.
   *    - SDP: Regular webRTC negotiation.
   *    - No SDP: Slow start webRTC negotiation.
   */
  if (sdp) {
    yield call(negotiation.handleUpdateRequest, deps, targetCall, { sdp, remoteNumber, remoteName })
  } else {
    yield call(negotiation.handleSlowUpdateRequest, deps, targetCall, { remoteNumber, remoteName })
  }
}

/**
 * A "call response" notification has been received and needs to be handled.
 *
 * This saga is intended to be the entry point for all "call response"
 *    notifications received from the call's remote side. A "call response"
 *    notification is the answer to an operation that was previously
 *    performed (a "call request"), and so represents the last step in the
 *    webRTC negotiation process.
 * This saga does not handle the response itself. It determines if it should be
 *    handled and what process is needed to handle it.
 * Responsibilities:
 *    1. Ensure this is a valid notification.
 *        - Call exists, Call is not ended, TODO: Call expects the response.
 *    2. Determine what negotiation process the response is from.
 *        - Regular or slow start negotiation process.
 *    3. Pass the request off to the appropriate handler.
 * @method parseCallResponse
 * @param  {Object} webRTC The webRTC stack.
 * @param {Object} deps         Dependencies that the saga uses.
 * @param {Object} params       Parameters describing the incoming call.
 * @param {string} [params.sdp] The remote SDP offer included with the notification (if any).
 * @param {string} params.wrtcsSessionId ID that the server uses to identify the session.
 * @param {string} params.remoteName   Name of the remote participant.
 * @param {string} params.remoteNumber Number of the remote participant.
 */
export function * parseCallResponse (deps, params) {
  const { wrtcsSessionId, sdp, customParameters } = params
  const targetCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)

  const log = logManager.getLogger('CALL', (targetCall || {}).id)
  log.info('Received new update call response; handling.', { wrtcsSessionId })

  if (!targetCall) {
    // Scenario: The notification is about a call that state does not know about.
    //    Ignore the notification.
    log.info('Update response for unknown wrtcsSession. Ignoring.', { wrtcsSessionId })
    return
  } else if (targetCall.state === CALL_STATES.ENDED) {
    const log = logManager.getLogger('CALL', targetCall.id)
    // Scenario: The notification is about a call that state says is ended.
    //    Ignore the notification, since ended calls should not have an active
    //    webRTC Session.
    log.info('Update response for ended call. Ignoring.', { wrtcsSessionId })
    return
  }
  // TODO: Make sure the call is expecting a `respondCallUpdate` notification.
  //    ie. has a pending operation.

  // Dispatch a custom parameters received action/event if any custom parameters were received as part of the notification
  if (customParameters) {
    const customNames = customParameters.map(param => param.name)
    log.debug(`Received custom parameters as part of the Call: ${customNames}.`)
    yield put(
      callActions.customParametersReceived(targetCall.id, {
        customParameters
      })
    )
  }

  /**
   * Check that the notification was not an "error" notification.
   * Status code 49 with a `retryAfter` value indicates the original operation
   *    failed (likely glare condition) and the SDK should retry the operation.
   * TODO: This check is currently Link-specific. Depending on how CPaaS
   *    indicates a failed/glare operation, either standardize this check or
   *    move this into Link-specific saga.
   */
  if (!sdp && params.code === 49 && params.retryAfter) {
    // TODO: Properly handle this scenario. The call may be in a bad state
    //    because we set an offer but not an answer. May need to revert
    //    signaling state and/or re-perform operation internally.
    // For now, fail loudly and abort the operation to prevent crashes. The
    //    negotiation sagas expect there to be an SDP.
    // The call state will be left in a broken state because of KAA-1607.
    log.error(`Response indicates an error scenario (${params.message}). Aborting operation.`)
    return
  }

  /**
   * Determine if the original operation was done locally or remotely. This
   *    indicates whether it was regular or slow start negotiation.
   * If it was from a remote slow start operation, it will be tracked in the
   *    call state.
   */
  if (targetCall.remoteOp && targetCall.remoteOp.operation === OPERATIONS.SLOW_START) {
    yield call(negotiation.handleSlowUpdateResponse, deps, targetCall, params)
  } else {
    yield call(negotiation.handleUpdateResponse, deps, targetCall, params)
  }
}

/**
 * A call status notification indicating other session has ended has been received and needs to be handled.
 *
 * This saga is intended to handle the call status notifications that indicates a call has ended.
 * This notification means that the session has ended on the other side and we need to end our session as well.
 * Handling the notification will cleanup the session's media and puts the call in ENDED state.
 * Responsibilities:
 *    1. Have the callstack perform the required webRTC cleanup process.
 *    2. Update call state to ENDED (via redux action).
 * @method callStatusUpdateEnded
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   params        Parameters describing the incoming call.
 * @param {string}   params.wrtcsSessionId ID that the server uses to identify the session.
 * @param {string}   [params.reasonText]   Human-readable explanation for the call change.
 * @param {string}   [params.statusCode]     Code representing the reason for the call change.
 */
export function * callStatusUpdateEnded (deps, params) {
  const { wrtcsSessionId, reasonText, statusCode } = params
  const currentCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)

  const log = logManager.getLogger('CALL', (currentCall || {}).id)
  log.info('Received call status ended notice; handling.', { wrtcsSessionId })

  if (!currentCall) {
    log.info('Call ended notice for unknown wrtcsSession. Ignoring.', { wrtcsSessionId })
    return
  }

  if (currentCall.state === CALL_STATES.ENDED) {
    log.info('Call ended notice for already Ended call. Ignoring.', { wrtcsSessionId })
    return
  }

  log.debug(`Call ended notice caused by ${reasonText} (Status Code: ${statusCode}).`)

  // Close the local webRTC session
  yield call(closeCall, deps.webRTC, currentCall.webrtcSessionId)

  log.info(`Finished handling call ended notice. Changing to ${CALL_STATES.ENDED}.`)
  // TODO: Don't expose these directly. Create our own convention so that transition data
  //       can be consistent across different operations.
  yield put(callActions.endCallFinish(currentCall.id, generateEndParams(currentCall.state, false, params)))
}

/**
 * This saga is intended to handle the call status notifications that indicates a session has completed.
 * A session has completed (in this context) means completing a midcall operation such as:
 *
 * - direct transfer
 * - consultative transfer
 * - join
 * , where user A transferred user B to user C.
 *
 * For the direct & consultative transfer operations, the 'sessionComplete' notification
 * will just refer to the original call between: A<->B, which has now completed.
 * Therefore this saga will send a 'DELETE' REST request to inform backend to tear down
 * this particular original call.
 *
 * Note that for the other original call, which is A<->C, server already knows
 * that it has ended and callStatusUpdateEnded saga (above) already handles it by simply
 * marking it locally as ended (i.e. in local Redux state). Therefore there is no need
 * to send any 'DELETE' REST request for A<->C call.
 *
 * For the join operation, SDK will receive two separate 'sessionComplete' notifications from backend.
 * Therefore this saga will send two 'DELETE' REST requests to inform backend to tear down those
 * two original calls.
 *
 * @method sessionStatusUpdateEnded
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Object}   params        Parameters describing the incoming call.
 * @param {string}   params.wrtcsSessionId ID that the server uses to identify the session.
 * @param {string}   [params.reasonText]   Human-readable explanation for the call change.
 * @param {string}   [params.statusCode]     Code representing the reason for the call change.
 */
export function * sessionStatusUpdateEnded (deps, params) {
  const { wrtcsSessionId, reasonText, statusCode } = params
  const requests = deps.requests

  const currentCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)

  const log = logManager.getLogger('CALL', (currentCall || {}).id)

  // Make sure the call state is what we expect
  if (!currentCall) {
    log.info('Session completed notice for unknown Call. Ignoring.')
    return
  }

  log.info('Received session status completed notice; handling.', { wrtcsSessionId })

  if (reasonText) {
    let customStatusCode = statusCode
    if (!statusCode && statusCode !== 0) {
      if (reasonText === COMPLEX_OPERATION_MESSAGES.DIRECT_TRANSFER_SUCCESS) {
        customStatusCode = STATUS_CODES.DIRECT_TRANSFER_SUCCESS
      } else if (reasonText === COMPLEX_OPERATION_MESSAGES.CONSULTATIVE_TRANSFER_SUCCESS) {
        customStatusCode = STATUS_CODES.CONSULTATIVE_TRANSFER_SUCCESS
      } else if (reasonText === COMPLEX_OPERATION_MESSAGES.JOIN_SUCCESS) {
        customStatusCode = STATUS_CODES.JOIN_SUCCESS
      }
    }
    log.debug(`Session completed notice caused by ${reasonText} (${customStatusCode}).`)
  }

  log.info(`Ending webRTC session with id: ${currentCall.webrtcSessionId}, locally ...`)

  // Close the local webRTC session
  yield call(closeCall, deps.webRTC, currentCall.webrtcSessionId)

  // If we had the local operation tracked in state, then we can dispatch the action
  //    intended to "finish" the operation.
  const localOp = currentCall.localOp
  if (localOp && localOp.operation && localOp.status === OP_STATUS.PENDING) {
    let transition
    let finishAction
    switch (localOp.operation) {
      case OPERATIONS.DIRECT_TRANSFER:
        finishAction = callActions.directTransferFinish
        transition = {
          reasonText: COMPLEX_OPERATION_MESSAGES.DIRECT_TRANSFER_SUCCESS,
          statusCode: STATUS_CODES.DIRECT_TRANSFER_SUCCESS
        }
        break
      case OPERATIONS.CONSULTATIVE_TRANSFER:
        finishAction = callActions.consultativeTransferFinish
        transition = {
          reasonText: COMPLEX_OPERATION_MESSAGES.CONSULTATIVE_TRANSFER_SUCCESS,
          statusCode: STATUS_CODES.CONSULTATIVE_TRANSFER_SUCCESS
        }
        break
      case OPERATIONS.JOIN:
        finishAction = callActions.joinFinish
        transition = {
          reasonText: COMPLEX_OPERATION_MESSAGES.JOIN_SUCCESS,
          statusCode: STATUS_CODES.JOIN_SUCCESS
        }
        break
      default:
        log.info('Unknown local call operation for completed notice. Cannot provide transition information.')
        break
    }
    if (finishAction) {
      yield put(finishAction(currentCall.id, { transition }))
    }
  }

  // We also need to notify the backend that call with currentCall.id should be removed
  // by sending a DELETE REST request.
  // Perform signalling to end the session on server's side.
  const isAnonymous = currentCall.isAnonymous
  const account = currentCall.account
  log.debug('Ending call session on server-side.')
  const response = yield call(requests.endSession, { wrtcsSessionId, isAnonymous, account })

  if (!response.error) {
    log.info(`Finished ending call. Changing call state to ${CALL_STATES.ENDED}.`)
    yield put(callActions.endCallFinish(currentCall.id, generateEndParams(currentCall.state, true, params)))
  } else {
    log.debug(
      `Error received when attempting to end the session: ${response.error}. Changing call state to ${CALL_STATES.ENDED}.`
    )
    yield put(
      callActions.endCallFinish(currentCall.id, {
        isLocal: true,
        error: response.error
      })
    )
  }
}

/**
 * A "call status update" has been received and needs to be handled.
 * The update specifies the server session is in "ringing" state.
 *
 * This saga handles the "call ringing" notification by updating the local
 *    session state to match the server session state. This only affects the
 *    local call state.
 * Responsibilities:
 *    1. Update call state to RINGING (via redux action).
 * @method callStatusUpdateRinging
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   params        Parameters describing the incoming call.
 * @param {string}   params.wrtcsSessionId ID that the server uses to identify the session.
 * @param {string}   params.remoteName   Name of the remote participant.
 * @param {string}   params.remoteNumber Number of the remote participant.
 */
export function * callStatusUpdateRinging (deps, params) {
  const { wrtcsSessionId, customParameters } = params
  const currentCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)

  const log = logManager.getLogger('CALL', (currentCall || {}).id)
  log.info('Received call status ringing notice; handling.', { wrtcsSessionId })

  if (!currentCall) {
    log.info('Call ringing notice for unknown wrtcsSession. Ignoring.', { wrtcsSessionId })
    return
  } else if (currentCall.state !== CALL_STATES.INITIATED) {
    log.info(`Call ringing notice for Call in an invalid state: ${currentCall.state}. Ignoring.`)
    return
  }

  // Dispatch a custom parameters received action/event if any custom parameters were received as part of the notification
  if (customParameters) {
    const customNames = customParameters.map(param => param.name)
    log.debug(`Received custom parameters as part of the Call: ${customNames}.`)
    yield put(
      callActions.customParametersReceived(currentCall.id, {
        customParameters
      })
    )
  }

  log.info(`Finished handling call ringing notice. Changing to ${CALL_STATES.RINGING}.`)
  yield put(
    callActions.callRinging(currentCall.id, {
      // Remote participant's information.
      remoteParticipant: {
        displayNumber: params.remoteNumber,
        displayName: params.remoteName
      }
    })
  )
}

/**
 * A call status notification indicating that an operation has failed and needs to be handled.
 *
 * This saga handles the "session fail" notification by generating a transition object
 *    containing proper statusCode and reasonText and removing the current pending operation.
 * Responsibilities:
 *    1. Generate transition object containing proper statusCode and reasonText depending on pending operation.
 * @method callStatusUpdateFailed
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   params        Parameters describing the incoming call.
 * @param {string}   params.wrtcsSessionId ID that the server uses to identify the session.
 */
export function * callStatusUpdateFailed (deps, params) {
  const { wrtcsSessionId, reasonText, statusCode } = params
  const currentCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)

  const log = logManager.getLogger('CALL', (currentCall || {}).id)
  log.info('Received call operation failed notice; handling.', { wrtcsSessionId })

  if (!currentCall) {
    log.info('Call operation failed notice for unknown wrtcsSession. Ignoring.', { wrtcsSessionId })
    return
  }

  const localOp = currentCall.localOp
  if (localOp && localOp.operation && localOp.status === OP_STATUS.PENDING) {
    let transition
    let finishAction
    switch (localOp.operation) {
      case OPERATIONS.DIRECT_TRANSFER:
        finishAction = callActions.directTransferFinish
        // TODO: May or may not need this when generic operation event is setup.
        transition = {
          reasonText: reasonText || COMPLEX_OPERATION_MESSAGES.DIRECT_TRANSFER_FAILURE,
          statusCode: statusCode || STATUS_CODES.DIRECT_TRANSFER_FAILURE
        }
        break
      case OPERATIONS.CONSULTATIVE_TRANSFER:
        finishAction = callActions.consultativeTransferFinish
        transition = {
          reasonText: reasonText || COMPLEX_OPERATION_MESSAGES.CONSULTATIVE_TRANSFER_FAILURE,
          statusCode: statusCode || STATUS_CODES.CONSULTATIVE_TRANSFER_FAILURE
        }
        break
      case OPERATIONS.JOIN:
        finishAction = callActions.joinFinish
        transition = {
          reasonText: reasonText || COMPLEX_OPERATION_MESSAGES.JOIN_FAILURE,
          statusCode: statusCode || STATUS_CODES.JOIN_FAILURE
        }
        break
    }
    log.debug(`Processing as a local ${localOp.operation} failure (${transition.statusCode}).`)

    if (finishAction) {
      log.info('Finished handling call operation failed notice.')
      yield put(
        finishAction(currentCall.id, {
          error: new BasicError({
            message: `Operation ${localOp.operation} failed.`,
            code: callCodes.GENERIC_ERROR
          }),
          transition
        })
      )
      return
    }
  }

  yield put(callActions.updateCall(currentCall.id))
}

/**
 * A "call cancelled" notification needs to be handled.
 *
 * A call can be "cancelled" for different reasons. In general, it means that
 *    the call was not established (locally) but it is no longer available for
 *    the user to respond. For example, it may have been answered on another
 *    device, or the caller may have ended it while it was ringing.
 *
 * The call is cancelled locally by cleaning up any/all Webrtc resources and
 *    changing the call state to indicate it was cancelled.
 *
 * @method callCancelled
 * @param {Object} deps         Dependencies that the saga uses.
 * @param {Object} deps.webRTC  The WebRTC stack.
 * @param {Object} params       Parameters describing the notification.
 */
export function * callCancelled (deps, params) {
  const { wrtcsSessionId } = params
  const targetCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)

  const log = logManager.getLogger('CALL', (targetCall || {}).id)
  log.info('Received call cancelled notice; handling.', { wrtcsSessionId })

  if (!targetCall) {
    log.info('Call cancelled notice for unknown wrtcsSession. Ignoring.', { wrtcsSessionId })
    return
  } else if ([CALL_STATES.ENDED, CALL_STATES.CANCELLED].includes(targetCall.state)) {
    log.info(`Call cancelled notice for already ${targetCall.state} call. Ignoring.`, { wrtcsSessionId })
    return
  }

  // Clean up the Webrtc portion of the Call.
  yield call(closeCall, deps.webRTC, targetCall.webrtcSessionId)

  log.info(`Finished handling call cancelled notice. Changing to ${CALL_STATES.CANCELLED}.`)
  // Dispatch an action to handle the redux portion of the Call.
  yield put(callActions.callCancelled(targetCall.id))
}

/**
 * A "session progress" notification has been received. It contains an "early
 *    media" SDP (pranswer) that needs to be processed.
 *
 * Processing a pranswer is done by:
 *    1. Validating the Call / Session.
 *    2. Running the SDP through the SDP pipeline.
 *    3. Setting the SDP as the Session's remote description (as pranswer).
 *    4. Updating the Call state.
 *
 * @method receiveEarlyMedia
 * @param {Object} deps         Dependencies that the saga uses.
 * @param {Object} deps.webRTC  The WebRTC stack.
 * @param {Object} params       Parameters describing the notification.
 */
export function * receiveEarlyMedia (deps, params) {
  const { wrtcsSessionId, customParameters } = params
  const { webRTC } = deps

  /**
   * Get the call from state.
   */
  const currentCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)

  const log = logManager.getLogger('CALL', (currentCall || {}).id)
  log.info('Received early media notice; handling.', { wrtcsSessionId })

  if (!currentCall) {
    log.info('Early media notice for unknown wrtcsSession. Ignoring.', { wrtcsSessionId })
    return
  }

  /**
   * Get the Webrtc Session.
   */
  const session = yield call([webRTC.sessionManager, 'get'], currentCall.webrtcSessionId)
  if (!session) {
    log.info('Early media notice for missing wrtcsSession. Ignoring.', { wrtcsSessionId })
    // TODO: Better error.
    return
  }

  // Dispatch a custom parameters received action/event if any custom parameters were received as part of the notification
  if (customParameters) {
    const customNames = customParameters.map(param => param.name)
    log.debug(`Received custom parameters as part of the Call: ${customNames}.`)
    yield put(
      callActions.customParametersReceived(currentCall.id, {
        customParameters
      })
    )
  }

  try {
    /*
     * Run the remote SDP pranswer through any SDP handlers provided, then set it
     *    as the Session's remote description.
     * This is the "pre set remote" stage.
     */
    const callConfigOptions = yield select(getOptions)
    const sdp = yield call(runPipeline, callConfigOptions.sdpHandlers, params.sdp, {
      callId: currentCall.id,
      type: 'pranswer',
      step: 'set',
      endpoint: 'remote'
    })
    yield call([session, 'processAnswer'], {
      type: 'pranswer',
      sdp: sdp
    })
  } catch (err) {
    log.debug(`Failed to process pranswer for Call ${currentCall.id}; ignoring.`)
    return
  }

  // Get the list of all remote tracks being offered in this call. This is a
  //    new call, so this should be the full list of remote tracks available.
  const remoteTracks = yield call(getAllRemoteTracks, session)

  log.info(`Finished handling early media notice. Changing to ${CALL_STATES.EARLY_MEDIA}.`)
  yield put(
    callActions.sessionProgress(currentCall.id, {
      // Remote participant's information.
      remoteParticipant: {
        displayNumber: params.remoteNumber,
        displayName: params.remoteName
      },
      remoteTracks
    })
  )
}

/**
 * Workaround saga: If a midcall negotiation is received before the call answer,
 *    this saga will delay the midcall negotiation until after the call is
 *    established. On a time-out of 3 seconds.
 * @method delayMidCall
 * @param {Object} deps   Deps that were provided to `parseCallRequest` saga.
 * @param {Object} params Params that were provided to `parseCallRequest` saga.
 */
function * delayMidCall (deps, params) {
  const targetCall = yield select(getCallByWrtcsSessionId, params.wrtcsSessionId)
  const log = logManager.getLogger('CALL', targetCall.id)

  function callStartPattern (action) {
    // Wait for the call to be established or ended.
    return [MAKE_CALL_FINISH, END_CALL_FINISH].includes(action.type) && action.payload.id === targetCall.id
  }

  const { answer } = yield race({
    answer: take(callStartPattern),
    timeout: delay(3000)
  })

  if (answer) {
    log.info('Replaying delayed update request.', { wrtcsSessionId: params.wrtcsSessionId })
    yield call(parseCallRequest, deps, params)
  } else {
    log.info('Timed-out delayed update request. Ignoring.', { wrtcsSessionId: params.wrtcsSessionId })
    // TODO: Send 491 response.
  }
}
